import React, { useState, useEffect } from 'react';
import { X, Download, CheckCircle, XCircle, AlertTriangle, Clock, RefreshCw, ExternalLink } from 'lucide-react';
import { getExportAttempts } from '../api';
import Modal from './Modal';

const STATUS_ICONS = {
    pending: Clock,
    in_progress: RefreshCw,
    completed: CheckCircle,
    failed: XCircle,
};

const STATUS_COLORS = {
    pending: 'text-yellow-400',
    in_progress: 'text-blue-400',
    completed: 'text-green-400',
    failed: 'text-red-400',
};

const STATUS_LABELS = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    failed: 'Failed',
};

const DESTINATION_LABELS = {
    s3: 'S3 / MinIO',
    sftp: 'SFTP',
    azure: 'Azure Blob',
    gcp: 'Google Cloud Storage',
    webhook: 'Webhook',
};

const ExportAttemptsModal = ({
    isOpen,
    onClose,
    driftEvent,
    addToast
}) => {
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedAttempt, setExpandedAttempt] = useState(null);

    useEffect(() => {
        if (isOpen && driftEvent) {
            loadAttempts();
        }
    }, [isOpen, driftEvent]);

    const loadAttempts = async () => {
        setLoading(true);
        try {
            const data = await getExportAttempts(driftEvent.id);
            setAttempts(data);
        } catch (error) {
            addToast('Failed to load export attempts', 'error');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const formatDuration = (startedAt, completedAt) => {
        if (!completedAt) return '-';
        const start = new Date(startedAt);
        const end = new Date(completedAt);
        const ms = end - start;

        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}m`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString();
    };

    const toggleExpand = (attemptId) => {
        setExpandedAttempt(expandedAttempt === attemptId ? null : attemptId);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Export Details" maxWidth="max-w-2xl">
            <div className="space-y-4">
                {/* Event Info */}
                {driftEvent && (
                    <div className="bg-[#1e1e1e] border-b border-[#3c3c3c] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 flex-shrink-0">
                        <div>
                            <p className="text-sm font-medium text-white">
                                Drift Event
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                                {driftEvent.partner_name || driftEvent.contract_name} • {formatDate(driftEvent.created_at)}
                            </p>
                        </div>
                        <button
                            onClick={loadAttempts}
                            disabled={loading}
                            className="p-2 text-gray-400 hover:text-gray-200"
                            title="Refresh"
                        >
                            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                )}

                {/* Attempts List */}
                {loading ? (
                    <div className="text-center py-8">
                        <RefreshCw size={24} className="animate-spin mx-auto text-gray-400" />
                        <p className="text-sm text-gray-500 mt-2">Loading export attempts...</p>
                    </div>
                ) : attempts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <Download size={32} className="mx-auto mb-2 opacity-50" />
                        <p>No export attempts yet</p>
                        <p className="text-sm">Exports will appear here after resolution</p>
                    </div>
                ) : (
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                        {attempts.map((attempt) => {
                            const Icon = STATUS_ICONS[attempt.status] || Clock;
                            const statusColor = STATUS_COLORS[attempt.status] || 'text-gray-400';
                            const statusLabel = STATUS_LABELS[attempt.status] || attempt.status;
                            const destLabel = DESTINATION_LABELS[attempt.destination_type] || attempt.destination_type;
                            const isExpanded = expandedAttempt === attempt.id;

                            return (
                                <div
                                    key={attempt.id}
                                    className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-xl shadow-sm overflow-hidden flex flex-col"
                                >
                                    <div
                                        onClick={() => toggleExpand(attempt.id)}
                                        className="p-3 border-b border-[#3c3c3c] hover:bg-[#2a2a2a] transition-colors cursor-pointer"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <Icon size={18} className={statusColor} />
                                                <div>
                                                    <p className="text-sm font-medium text-white">
                                                        {attempt.destination_name || destLabel}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {destLabel} • Format: {attempt.export_format?.toUpperCase() || 'JSON'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColor.replace('bg-', 'bg-').replace('text-', 'text-').replace('text-', 'text-').replace('400', '400').replace('600', '600').replace('700', '700')}`}>
                                                    {statusLabel}
                                                </span>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {formatDuration(attempt.started_at, attempt.completed_at)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="border-t border-[#3c3c3c] p-3 bg-[#1e1e1e]/50">
                                            <div className="space-y-2 text-sm">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <span className="text-gray-500">Started:</span>
                                                        <span className="ml-2 text-white">
                                                            {formatDate(attempt.started_at)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Completed:</span>
                                                        <span className="ml-2 text-white">
                                                            {formatDate(attempt.completed_at)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {attempt.error_message && (
                                                    <div className="bg-red-900/20 p-2 rounded text-red-400">
                                                        <AlertTriangle size={14} className="inline mr-1" />
                                                        {attempt.error_message}
                                                    </div>
                                                )}

                                                {attempt.destination_path && (
                                                    <div>
                                                        <span className="text-gray-500">Path:</span>
                                                        <code className="ml-2 text-xs bg-gray-700 px-2 py-0.5 rounded">
                                                            {attempt.destination_path}
                                                        </code>
                                                    </div>
                                                )}

                                                {attempt.attempt_count > 1 && (
                                                    <div>
                                                        <span className="text-gray-500">Retry attempts:</span>
                                                        <span className="ml-2">{attempt.attempt_count}</span>
                                                    </div>
                                                )}

                                                {attempt.status === 'failed' && (
                                                    <div className="pt-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                addToast('Retry functionality coming soon', 'info');
                                                            }}
                                                            className="text-sm text-blue-400 hover:text-blue-300"
                                                        >
                                                            <RefreshCw size={14} className="inline mr-1" />
                                                            Retry Export
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Footer */}
                <div className="flex justify-end pt-4 border-t border-[#3c3c3c]">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ExportAttemptsModal;
